"use client";

import "./tailwind_base.css";
import { domAnimation, LazyMotion, m } from "framer-motion";
import { ArrowLeft, Home } from "lucide-react";
import { Geist, Inter, Jost, Raleway, Syne } from "next/font/google";
import Link from "next/link";
import { NoiseOverlay } from "@/components/custom/noise_overlay";
import { Button } from "@/components/ui/button";
import {
	SCROLL_LOCK_FULL_WIDTH_CLASS,
	SCROLL_LOCK_VIEWPORT_WIDTH_CLASS,
} from "@/lib/scroll-lock";

const geistSans = Geist({
	variable: "--font-geist-sans",
	subsets: ["latin"],
});

const raleway = Raleway({
	variable: "--font-raleway",
	subsets: ["latin"],
});

const syne = Syne({
	variable: "--font-syne",
	subsets: ["latin"],
});

const inter = Inter({
	variable: "--font-inter",
	subsets: ["latin"],
});

const jost = Jost({
	variable: "--font-jost",
	subsets: ["latin"],
});

export default function NotFound() {
	return (
		<LazyMotion features={domAnimation}>
			<div
				className={`${raleway.className} ${geistSans.className} ${syne.variable} ${inter.variable} ${jost.variable} antialiased min-h-screen bg-white text-zinc-900 font-sans w-full relative overflow-hidden selection:bg-black selection:text-white flex items-center justify-center`}
			>
				{/* Background Elements */}
				<NoiseOverlay />
				<div
					className={`fixed inset-0 z-0 pointer-events-none ${SCROLL_LOCK_FULL_WIDTH_CLASS} ${SCROLL_LOCK_VIEWPORT_WIDTH_CLASS}`}
				>
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-zinc-100 to-transparent opacity-60 rounded-full blur-3xl animate-pulse" />
				</div>

				<div className="relative z-10 w-full max-w-3xl mx-auto px-4 text-center">
					<m.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
						className="flex flex-col items-center"
					>
						{/* 404 Display - Reduced margin bottom to tighten layout since container is gone */}
						<div className="font-syne font-black text-9xl text-zinc-950/10 mb-[-30px] select-none">
							404
						</div>

						<h1 className="font-syne font-bold text-5xl md:text-7xl uppercase tracking-tighter leading-none mb-8 text-zinc-950 relative z-20">
							Page Not Found
						</h1>

						<p className="font-inter text-zinc-500 text-lg md:text-xl max-w-lg mx-auto leading-relaxed font-medium mb-12">
							The page you are looking for has been moved, deleted, or possibly
							never existed.
						</p>

						<div className="flex flex-col sm:flex-row gap-5 justify-center items-center w-full sm:w-auto">
							<Button
								onClick={() => window.history.back()}
								variant="outline"
								className="h-14 px-10 rounded-xl font-syne font-bold uppercase tracking-wide border-2 border-zinc-200 hover:bg-zinc-100 hover:text-zinc-900 transition-[color,background-color,transform] hover:-translate-y-0.5 w-full sm:w-auto text-base"
							>
								<ArrowLeft className="w-5 h-5 mr-2" />
								Go Back
							</Button>

							<Button
								asChild
								className="h-14 px-10 rounded-xl font-syne font-bold uppercase tracking-wide bg-zinc-900 text-white hover:bg-red-600 transition-[background-color,box-shadow,transform] shadow-lg hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-auto text-base"
							>
								<Link href="/">
									<Home className="w-5 h-5 mr-2" />
									Return Home
								</Link>
							</Button>
						</div>
					</m.div>

					{/* Footer decoration */}
					<div className="mt-20">
						<p className="font-inter text-xs font-bold text-zinc-300 tracking-[0.2em] uppercase">
							System Error • 404
						</p>
					</div>
				</div>
			</div>
		</LazyMotion>
	);
}
